// Small component that updates the DOM (progress bar / countdown text) using refs and
// requestAnimationFrame to avoid causing a re-render of the whole ScreenMapAsset component

import React, { useEffect, useRef } from "react";
import { APP_CONFIG } from "../../constants/constants";

const RefreshCountdownBar: React.FC<{
  nextRefreshAtRef: React.MutableRefObject<number | null>;
  refreshTotalMsRef: React.MutableRefObject<number>;
  refreshPaused: boolean;
  userMapRefreshing: boolean;
  translationFn?: (k: string) => string;
  refreshFrequency?: string;
  expanded?: boolean;
}> = React.memo(
  ({
    nextRefreshAtRef,
    refreshTotalMsRef,
    refreshPaused,
    userMapRefreshing,
    translationFn,
    refreshFrequency,
    expanded = false,
  }) => {
    const barRef = useRef<HTMLDivElement | null>(null);
    const textRef = useRef<HTMLDivElement | null>(null);
    const rafLocal = useRef<number | null>(null);

    useEffect(() => {
      if (rafLocal.current) {
        cancelAnimationFrame(rafLocal.current);
        rafLocal.current = null;
      }

      if (refreshPaused) {
        // ensure paused look
        if (barRef.current) {
          barRef.current.style.width = `${100}%`;
          barRef.current.style.backgroundColor = "#CCCCCC";
        }
        if (textRef.current) {
          textRef.current.textContent = translationFn
            ? translationFn("updates_paused_status")
            : "Paused";
        }
        return () => null;
      }

      const loop = () => {
        const nextAt = nextRefreshAtRef.current;
        if (!nextAt) {
          rafLocal.current = requestAnimationFrame(loop);
          return;
        }
        const remaining = Math.max(0, nextAt - Date.now());
        const pct = Math.round((remaining / refreshTotalMsRef.current) * 100);
        if (barRef.current) {
          barRef.current.style.width = `${Math.max(0, pct)}%`;
          barRef.current.style.backgroundColor = refreshPaused
            ? "#9CA3AF" // grey
            : Math.max(0, pct) < 25 // userMapRefreshing
              ? "#F59E0B" // orange
              : "#10B981"; // green
        }
        if (textRef.current) {
          textRef.current.textContent = `${Math.ceil(
            (refreshTotalMsRef.current * Math.max(0, pct)) / 100 / 1000
          )}s`;
        }
        rafLocal.current = requestAnimationFrame(loop);
      };

      rafLocal.current = requestAnimationFrame(loop);

      return () => {
        if (rafLocal.current) cancelAnimationFrame(rafLocal.current);
        rafLocal.current = null;
      };
    }, [refreshPaused, userMapRefreshing, refreshFrequency]);

    return (
      <div className="w-auto absolute top-0 left-[2px] right-[2px] pointer-events-none touch-none">
        <div className="h-1 bg-gray-200 Xrounded rounded-tl rounded-tr overflow-hidden">
          <div
            ref={barRef}
            className="h-full Xrounded transition-all duration-200"
            style={{ width: `100%` }}
          />
        </div>
        {APP_CONFIG.DEBUG.ALL &&
          !!expanded &&
          translationFn &&
          !refreshPaused && (
            <div
              ref={textRef}
              className="absolute top-[18px] right-11 text-xs text-gray-500"
            >
              {translationFn
                ? translationFn("updates_paused_status")
                : "Paused"}
              {/* Paused does not display (and that's fine) */}
            </div>
          )}
      </div>
    );
  }
);

export default RefreshCountdownBar;
